import i18n from "../../i18n";

import Logo from "../../assets/GrECo_Logo_small.jpg";

import { getUserPhotoSmall, logout } from "../../service/GraphService";
import { getHeaderConfig } from "./utils";

import { useTranslation } from "react-i18next";

import GrecoHeader from "components/GrecoHeader/GrecoHeader";
import { NavigationContent } from "components/Navigation/NavigationContent";
import { useGetUserAppTargetSetting } from "hooks/useGetUserAppTargetSetting";
import { useIsDarkMode } from "hooks/useIsDarkMode";
import useNotificationHandler from "hooks/useNotificationHandler";
import { useSetAtom } from "jotai";
import { APP_ID, APP_SETTING_ID } from "library/constants";
import { EAppSettings, SelectItem } from "library/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAddAppSetting } from "state/queries/useAddAppSetting";
import { useUpdateAppSettings } from "state/queries/useUpdateAppSettings";
import { useUserData } from "state/queries/useUserData";
import { buildYupLocale } from "validation";
import { panelIsOpenState } from "../../store";

import useGetLanguageOptionsPerUser from "hooks/useGetLanguageOptionsPerUser";
import { Button } from "@fluentui/react-components";
import { setAskPrincipal } from "help/ask-client";
import { HelpLauncher, HelpNotice } from "help/HelpLauncher";
import { helpLanguageFor, HelpViewer } from "help/types";
import { useAppTheme } from "hooks/useAppTheme";
import { useRoleType } from "hooks/useRoleType";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const AppHeader = () => {
  const { t } = useTranslation();
  const { notifications, handleClearAllNotifications } =
    useNotificationHandler();

  const languageOptions = useGetLanguageOptionsPerUser();

  const setPanelIsOpen = useSetAtom(panelIsOpenState);
  const darkMode = useIsDarkMode();
  const theme = useAppTheme();

  const { data: user } = useUserData();

  const [userWithPhoto, setUserWithPhoto] = useState<any>(null);

  useEffect(() => {
    (async () => {
      const imgUrl = await getUserPhotoSmall(null);
      setUserWithPhoto({
        ...user,
        photoSmall: imgUrl,
      });
    })();
  }, [user]);

  const { mutate: onAddAppSetting } = useAddAppSetting();
  const { mutate: onUpdateAppSetting } = useUpdateAppSettings();
  const languageAppSetting = useGetUserAppTargetSetting({
    targetSettingName: EAppSettings.language,
  });

  const headerConfig = getHeaderConfig(t("hbh.appName.label"));

  const { pathname } = useLocation();
  const { hasHrReadOnly, hasAdminRole, hasSuperAdminRole } = useRoleType();
  const helpViewer = useMemo<HelpViewer>(
    () => ({
      canEditHr: !hasHrReadOnly,
      isAdmin: !!hasAdminRole,
      isSuperAdmin: !!hasSuperAdminRole,
    }),
    [hasHrReadOnly, hasAdminRole, hasSuperAdminRole]
  );
  // The Ask tab rank-boosts answers for the viewer's tiers (never hides one).
  useEffect(() => {
    setAskPrincipal({
      roles: [
        "user",
        ...(helpViewer.canEditHr ? ["hr"] : []),
        ...(helpViewer.isAdmin ? ["admin"] : []),
        ...(helpViewer.isSuperAdmin ? ["superadmin"] : []),
      ],
    });
  }, [helpViewer]);

  const notifyHelp = useCallback((notice: HelpNotice) => {
    const show =
      notice.severity === "warning"
        ? toast.warn
        : notice.severity === "success"
        ? toast.success
        : toast.info;
    const { action } = notice;
    show(
      action
        ? ({ closeToast }) => (
            <span>
              {notice.message}{" "}
              <Button
                size="small"
                appearance="transparent"
                onClick={() => {
                  action.onClick();
                  closeToast?.();
                }}
              >
                {action.label}
              </Button>
            </span>
          )
        : notice.message,
      { autoClose: notice.durationMs ?? 5000 }
    );
  }, []);

  return (
    <GrecoHeader
      navigation={<NavigationContent />}
      headerConfig={headerConfig}
      panelStatus={setPanelIsOpen}
      user={userWithPhoto}
      notifications={notifications}
      clearNotifications={handleClearAllNotifications}
      logo={Logo}
      logout={logout}
      darkMode={darkMode}
      helpLauncher={
        <HelpLauncher
          pathname={pathname}
          viewer={helpViewer}
          language={helpLanguageFor(i18n.language)}
          notify={notifyHelp}
          iconColor={theme.palette.purpleDark}
        />
      }
      languageOptions={languageOptions}
      defaultLanguage={i18n.language}
      onChangeLanguage={async (__event: any, languageOption?: SelectItem) => {
        if (!languageOption) return;

        i18n.changeLanguage(languageOption.value as string);

        if (languageAppSetting) {
          onUpdateAppSetting({
            payload: [
              {
                ...languageAppSetting,
                userAppSettingValue: languageOption?.value?.toString() || "",
              },
            ] as any,
          });
        } else {
          onAddAppSetting({
            payload: [
              {
                applicationCodeId: APP_ID,
                userAppSettingValue: languageOption?.value?.toString() || "",
                appSettingCodeId: APP_SETTING_ID,
                userAppSettingName: EAppSettings.language,
                isDefault: false,
              },
            ],
          });
        }

        buildYupLocale(null, t);
      }}
      headerStrings={{
        notificationsPanelStrings: {
          panelTitle: t("header.Notifications"),
          noNotificationsTitle: t("header.NoNotificationsTitle"),
          noNotificationsSubtitle: t("header.NoNotificationsSubtitle"),
        },
      }}
    />
  );
};

export default AppHeader;
